import { useState, useEffect } from 'react';
import { Dialog } from '@economic/taco';
import { ProgressDots } from '~/app/shared/components/ui/progressDots/ProgressDots';
import { useNavigate, useLocation } from 'react-router-dom';
import useQuery from '~/app/shared/hooks/useQuery';
import type { CompanyResponse } from '~/payment-cards-feature';
import { useCurrentAgreement } from '@economic/react-app';
import { WelcomeStep } from './steps/step-1-welcome/WelcomeStep';
import { CompanyInfoStep } from './steps/step-2-company-info/CompanyInfoStep';
import { BankAccountStep } from './steps/step-3-bank-account/BankAccountStep';
import { SuccessStep } from './steps/step-4-success/SuccessStep';
import { CancelStep } from './steps/CancelStep';

enum OnboardingStep {
    WELCOME = 'Welcome',
    COMPANY_INFO = 'CompanyInfo',
    BANK_ACCOUNT = 'BankAccount',
    SUCCESS = 'Success',
}

const ONBOARDING_STEPS = {
    [OnboardingStep.WELCOME]: {
        step: OnboardingStep.WELCOME,
        nextStep: OnboardingStep.COMPANY_INFO,
        stepNumber: 1,
    },
    [OnboardingStep.COMPANY_INFO]: {
        step: OnboardingStep.COMPANY_INFO,
        nextStep: OnboardingStep.BANK_ACCOUNT,
        stepNumber: 2,
    },
    [OnboardingStep.BANK_ACCOUNT]: {
        step: OnboardingStep.BANK_ACCOUNT,
        nextStep: OnboardingStep.SUCCESS,
        stepNumber: 3,
    },
    [OnboardingStep.SUCCESS]: {
        step: OnboardingStep.SUCCESS,
        nextStep: OnboardingStep.SUCCESS,
        stepNumber: 4,
    },
};

export const PAYROLL_ONBOARDING_DIALOG_HASH = '#payroll-onboarding-dialog';

export const PayrollOnboardingDialog = () => {
    const agreement = useCurrentAgreement();
    const [isOpen, setIsOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState<OnboardingStep>(OnboardingStep.WELCOME);
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

    const { data: currentCompanyData, refetch: refetchCompanyData } = useQuery<CompanyResponse>(
        '/company/getCurrentCompany',
        null
    );

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        setIsOpen(location.hash === PAYROLL_ONBOARDING_DIALOG_HASH);
    }, [location.hash]);

    const handleClose = () => {
        // Show cancel confirmation instead of closing immediately
        setShowCancelConfirmation(true);
    };

    const handleConfirmClose = () => {
        setShowCancelConfirmation(false);
        setIsOpen(false);
        navigate(location.pathname);
    };

    const handleContinueOnboarding = () => {
        setShowCancelConfirmation(false);
    };

    const handleNext = () => {
        setCurrentStep(ONBOARDING_STEPS[currentStep].nextStep);
    };

    const renderStepContent = () => {
        if (showCancelConfirmation) {
            return <CancelStep onCancel={handleConfirmClose} onContinue={handleContinueOnboarding} />;
        }

        switch (currentStep) {
            case OnboardingStep.WELCOME:
                return <WelcomeStep handleNext={handleNext} />;
            case OnboardingStep.COMPANY_INFO:
                return (
                    <CompanyInfoStep
                        currentCompanyData={currentCompanyData}
                        refetchCompanyData={refetchCompanyData}
                        handleNext={handleNext}
                    />
                );
            case OnboardingStep.BANK_ACCOUNT:
                return <BankAccountStep handleNext={handleNext} />;
            case OnboardingStep.SUCCESS:
                return <SuccessStep />;
            default: {
                const exhaustiveCheck: never = currentStep;
                return exhaustiveCheck;
            }
        }
    };

    if (!agreement.features.payroll) {
        return null;
    }

    return (
        <Dialog open={isOpen} onClose={handleClose} size="sm">
            <Dialog.Content aria-label="Payroll Onboarding" className="relative w-auto max-w-3xl">
                <Dialog.Title>
                    {!showCancelConfirmation && (
                        <ProgressDots
                            stepsCount={Object.keys(ONBOARDING_STEPS).length}
                            currentStep={ONBOARDING_STEPS[currentStep].stepNumber}
                            withCompleteTicks={false}
                        />
                    )}
                </Dialog.Title>

                <div className="mt-6">{renderStepContent()}</div>
            </Dialog.Content>
        </Dialog>
    );
};
